/**
 * Extracts error information from API responses that may return HTTP 200
 * but contain an internal error (status_code/statusCode 4xx/5xx) in the body.
 *
 * @param {Object} responseData - The full response body from the API (parsed JSON)
 * @param {string} [defaultErrorMessage='An error occurred.'] - Fallback message when none found
 * @returns {Object|null} If error: { message, statusCode, responseData }; if success: null
 */
export const extractApiError = (
  responseData,
  defaultErrorMessage = 'An error occurred.',
) => {
  if (!responseData) {
    return { message: 'Invalid response from server', statusCode: 500, responseData: null };
  }

  const statusCode = responseData.status_code ?? responseData.statusCode ?? 200;
  const msgCode = responseData.msg_code ?? responseData.msgCode ?? [];

  if (statusCode >= 200 && statusCode < 300) return null;

  const errorMessage =
    msgCode.length > 0
      ? (msgCode[0].description ?? msgCode[0].code ?? defaultErrorMessage)
      : defaultErrorMessage;

  return { message: errorMessage, statusCode, responseData };
};

/**
 * Validates an API response and either returns a normalized object or throws an Error.
 *
 * @param {Object} responseData - The full response body from the API
 * @param {string} [defaultErrorMessage='An error occurred.'] - Fallback message
 * @returns {Object} Normalized response: { status_code, msg_code, body, meta, request_id }
 * @throws {Error} When internal status_code is not 2xx
 */
export const validateApiResponse = (
  responseData,
  defaultErrorMessage = 'An error occurred.',
) => {
  const error = extractApiError(responseData, defaultErrorMessage);
  if (error) {
    const apiError = new Error(error.message);
    apiError.response = { data: error.responseData, status: error.statusCode };
    throw apiError;
  }

  return {
    status_code: responseData.status_code ?? responseData.statusCode ?? 200,
    msg_code: responseData.msg_code ?? responseData.msgCode ?? [],
    body: responseData.body ?? null,
    meta: responseData.meta ?? null,
    request_id: responseData.request_id ?? responseData.requestId ?? null,
  };
};

/**
 * Normalizes a pagination `meta` object into a single camelCase shape,
 * accepting either snake_case or camelCase from the backend.
 *
 * @param {Object|null|undefined} meta
 * @returns {{ totalItems: number, totalPages: number, currentPage: number, perPage: number, hasNext: boolean, hasPrevious: boolean, nextCursor: string|null }|null}
 */
export const normalizePagedMeta = (meta) => {
  if (!meta || typeof meta !== 'object') return null;

  const totalItems = meta.totalItems ?? meta.total_items ?? 0;
  const perPage = meta.perPage ?? meta.per_page ?? meta.page_size ?? 0;
  const currentPage = meta.currentPage ?? meta.current_page ?? meta.page ?? 1;
  const totalPages =
    meta.totalPages ??
    meta.total_pages ??
    (perPage > 0 ? Math.max(1, Math.ceil(totalItems / perPage)) : 0);

  return {
    totalItems,
    totalPages,
    currentPage,
    perPage,
    hasNext: meta.hasNext ?? meta.has_next ?? false,
    hasPrevious: meta.hasPrevious ?? meta.has_previous ?? false,
    nextCursor: meta.nextCursor ?? meta.next_cursor ?? null,
  };
};
